import { AppId, AppTextFieldHandler } from "../../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { Box, Checkbox, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Select, Tab, TextField, Typography } from "@mui/material"
import { Localized, useLocalization } from "@fluent/react"
import { If } from "../../../../app/If"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { useCallback, useState } from "react"
import { createSysMailTemplate, selectSysMailTemplateById, updateSysMailTemplate } from "./sysMailTemplatesSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import { SysMailFooter } from "../sysFooters/sysMailFootersSlice"
import { TabContext, TabList, TabPanel } from "@mui/lab"


export interface SysMailTemplateDialogProps {
    id: AppId | undefined
    sysMailFooters: SysMailFooter[];
    editable: boolean
    onSaveSuccess: () => void
    onCancel: () => void
}

export const SysMailTemplateDialog = ({ id, sysMailFooters, editable,  onSaveSuccess, onCancel }: SysMailTemplateDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const entity = useAppSelector(state => selectSysMailTemplateById(state, id ?? ""));
    const [type, setType] = useState<string>(entity?.type ?? "");
    const [context, setContext] = useState<string>(entity?.context ?? "");
    const [subject, setSubject] = useState<string>(entity?.subject ?? "");
    const [subjectEN, setSubjectEN] = useState<string>(entity?.subjectEN ?? "");
    const [body, setBody] = useState<string>(entity?.body ?? "");
    const [bodyEN, setBodyEN] = useState<string>(entity?.bodyEN ?? "");
    const [isFixed, setIsFixed] = useState<boolean>(entity?.isFixed ?? false);
    const [isInternal, setIsInternal] = useState<boolean>(entity?.isInternal ?? false);
    const [saving, setSaving] = useState(false)
    const [tab, setTab] = useState<"polish" | "english">("polish");

    const handleTypeChange: AppTextFieldHandler = useCallback(e => setType(e.target.value), [setType]);
    const handleContextChange = useCallback(e => setContext(e.target.value), [setContext]);
    const handleSubjectChange: AppTextFieldHandler = useCallback(e => setSubject(e.target.value), [setSubject]);
    const handleSubjectENChange: AppTextFieldHandler = useCallback(e => setSubjectEN(e.target.value), [setSubjectEN]);
    const handleBodyChange: AppTextFieldHandler = useCallback(e => setBody(e.target.value), [setBody]);
    const handleBodyENChange: AppTextFieldHandler = useCallback(e => setBodyEN(e.target.value), [setBodyEN]);
    const handleIsFixedChange = (event) => setIsFixed(event.target.checked);
    const handleIsInternalChange = (event) => setIsInternal(event.target.checked);

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])


    const handleSaveClick = async () => {
        setSaving(true)
        try {
            if(id === undefined && editable) {
                let template = {
                    type, context, subject, subjectEN, body, bodyEN, isFixed, isInternal
                };

                await dispatch(createSysMailTemplate(template)).unwrap()

                dispatch(showSuccess("saved"));
                onSaveSuccess()
            } else if (editable && entity) {
                let template = {
                    ...entity, type, context, subject, subjectEN, body, bodyEN, isFixed, isInternal
                };

                await dispatch(updateSysMailTemplate(template)).unwrap()

                dispatch(showSuccess("saved"));
                onSaveSuccess()
            }
            
        }
        catch (error) {
            dispatch(showError("error"));
        }
        finally {
            setSaving(false)
        }
    }

    return <>
        <DialogTitle>
            <If condition={id !== undefined} otherwise={
                <Localized id="new-mail-template">
                    <span>_Nowy szablon</span>
                </Localized>
            }>
                <Localized id="edit-mail-template">
                    <span>_Edycja szablonu</span>
                </Localized>
            </If>
        </DialogTitle>
        <DialogContent>
            <Grid container spacing={2} padding={2}>
                <Grid item xs={12} md={6} sx={{ overflow: 'auto', padding: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={editable ? 6 : 12}>
                            <TextField
                                fullWidth
                                disabled={id !== undefined || !editable}
                                value={type}
                                inputProps={{ maxLength: 100 }}
                                onChange={handleTypeChange}
                                label={<Localized id="mail-template-type">_Typ</Localized>}
                            />
                        </Grid>
                        <If condition={editable}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    disabled
                                    value={l10n.getString(`mail-type-${type}`, null, `_${type}`)}
                                    label={<Localized id="mail-template-name">_Nazwa</Localized>}
                                />
                            </Grid>
                        </If>
                        <Grid item xs={12}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel id="select-mail-context-label"><Localized id="mail-context">_Kontekst</Localized></InputLabel>
                                <Select
                                    labelId="select-mail-context-label"
                                    value={context}
                                    onChange={handleContextChange}
                                    label={<Localized id="mail-context">_Kontekst</Localized>}
                                    disabled={!editable}
                                >
                                    <MenuItem value={'DOCUMENT'}>
                                        <Localized id="mail-document">_Dokument</Localized>
                                    </MenuItem>
                                    <MenuItem value={'SYSTEM'}>System</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TabContext value={tab}>
                                <TabList onChange={handleTabChange}>
                            <Tab
                                key={0}
                                value={"polish"}
                                label={<Localized id="polish">_Polski</Localized>} />
                            <Tab
                                key={1}
                                value={"english"}
                                label={<Localized id="english">_Angielski</Localized>} />
                                </TabList>
                                <TabPanel value="polish">
                                    <TextField
                                        sx={{ marginTop: 2 }}
                                        fullWidth
                                        value={subject}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={handleSubjectChange}
                                        label={<Localized id="mail-subject">_Temat PL</Localized>}
                                    />
                                    <TextField
                                        sx={{ marginTop: 2 }}
                                        fullWidth
                                        value={body}
                                        minRows={3}
                                        maxRows={12}
                                        multiline
                                        inputProps={{ maxLength: 10000 }}
                                        onChange={handleBodyChange}
                                        label={<Localized id="mail-body">_Treść PL</Localized>}
                                    />
                                </TabPanel>
                                <TabPanel value="english">
                                    <TextField
                                        sx={{ marginTop: 2 }}
                                        fullWidth
                                        value={subjectEN}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={handleSubjectENChange}
                                        label={<Localized id="mail-subject-en">_Temat EN</Localized>}
                                    />
                                    <TextField
                                        sx={{ marginTop: 2 }}
                                        fullWidth
                                        value={bodyEN}
                                        minRows={3}
                                        maxRows={12}
                                        multiline
                                        inputProps={{ maxLength: 10000 }}
                                        onChange={handleBodyENChange}
                                        label={<Localized id="mail-body-en">_Treść EN</Localized>}
                                    />
                                </TabPanel>
                            </TabContext>
                        </Grid>
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={isFixed} onChange={handleIsFixedChange} />}
                                    label={<Localized id="sys-mail-templates-is-fixed"><span>_Nieedytowalne</span></Localized>}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={isInternal} onChange={handleIsInternalChange} />}
                                    label={<Localized id="sys-mail-templates-is-internal"><span>_Wewnętrzny</span></Localized>}
                                />
                            </FormGroup>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={6} sx={{ overflow: 'auto' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        <Localized id="mail-preview-full">_Podgląd całego maila</Localized>
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <strong><Localized id="mail-subject-label">_Temat:</Localized></strong>{' '}
                        {tab === "polish" ? subject : subjectEN}
                    </Box>
                    <Box sx={{ p: 2, border: '1px solid #ccc', backgroundColor: '#f9f9f9', mb: 2 }}>
                        <div dangerouslySetInnerHTML={{ __html: (tab === "polish" ? body : bodyEN) + (sysMailFooters[0]?.footer ?? '') }} />
                    </Box>
                    {sysMailFooters[0] && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6">
                                <Localized id="mail-preview-footer-only">_Podgląd tylko stopki</Localized>
                            </Typography>
                            <Box sx={{ p: 2, border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}>
                                <div dangerouslySetInnerHTML={{ __html: sysMailFooters[0].footer }} />
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <SaveButton loading={saving} onClick={handleSaveClick} />
            <CancelButton loading={saving} onClick={onCancel} />
        </DialogActions>
    </>
}